import React, { useEffect, useState } from "react";
import { fetchVideos, createVideo, fetchYouTubeReviews } from "@/lib/api";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import { useLocale } from "@/lib/locale";
import { Button } from "@/components/ui/button";
import { Plus, Youtube, Instagram, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

function VideoEmbed({ v }) {
  if (v.platform === "youtube" || v.embed_url?.includes("youtube.com/embed")) {
    return (
      <iframe
        src={v.embed_url}
        className="w-full h-full"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        title={v.title || v.video_title || v.critic_name || "Critic review"}
      />
    );
  }
  return (
    <a href={v.url} target="_blank" rel="noopener noreferrer" className="block h-full w-full grid place-items-center text-pink-400 hover:text-pink-300">
      <div className="text-center">
        <Instagram className="h-10 w-10 mx-auto mb-2" />
        <div className="font-heading text-sm">Open Instagram review →</div>
      </div>
    </a>
  );
}

export default function CriticVideos({ titleId }) {
  const { t } = useLocale();
  const { user } = useAuth() || {};
  const [videos, setVideos] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSugg, setLoadingSugg] = useState(true);
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");
  const [critic, setCritic] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => fetchVideos(titleId).then(setVideos).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [titleId]);

  useEffect(() => {
    setLoadingSugg(true);
    fetchYouTubeReviews(titleId)
      .then((items) => setSuggested(items || []))
      .catch(() => {})
      .finally(() => setLoadingSugg(false));
  }, [titleId]);

  const submit = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      await createVideo(titleId, url, "", critic);
      setUrl(""); setCritic(""); setAdding(false);
      load();
      toast.success("Review added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid URL");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="critic-videos">
      <div className="flex items-end justify-between mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-1 flex items-center gap-1.5">
            <Youtube className="h-3 w-3" /> {t("critic_videos")}
          </div>
          <h2 className="font-display text-3xl text-white">{t("critic_videos_sub")}</h2>
        </div>
        {user && !adding && (
          <Button
            size="sm"
            variant="outline"
            className="border-amber/40 text-amber hover:bg-amber/10 hover:text-amber-hover font-heading"
            onClick={() => setAdding(true)}
            data-testid="add-video-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> {t("add_video")}
          </Button>
        )}
      </div>

      {adding && (
        <div className="my-4 glass rounded-md p-4 space-y-2">
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t("embed_video")}
            className="w-full bg-black/30 border border-white/10 rounded-md p-3 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-amber/50"
            data-testid="video-url-input" />
          <input value={critic} onChange={(e) => setCritic(e.target.value)} placeholder="Critic name (optional)"
            className="w-full bg-black/30 border border-white/10 rounded-md p-3 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-amber/50"
            data-testid="video-critic-input" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setAdding(false); setUrl(""); setCritic(""); }}>Cancel</Button>
            <Button disabled={busy} onClick={submit} className="bg-amber hover:bg-amber-hover text-obsidian font-heading" data-testid="video-submit-btn">
              {t("submit")}
            </Button>
          </div>
        </div>
      )}

      {!user && (
        <p className="text-zinc-500 text-sm font-body italic mb-4">
          {t("sign_in_to_post")} · <button onClick={startGoogleLogin} className="text-amber hover:underline">{t("sign_in")}</button>
        </p>
      )}

      {/* User-submitted reviews */}
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-amber" /></div>
      ) : videos.length > 0 ? (
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {videos.map((v) => (
            <div key={v.id} className="border border-amber/30 rounded-md overflow-hidden bg-obsidian-surface/60" data-testid={`video-${v.id}`}>
              <div className="relative aspect-video bg-black"><VideoEmbed v={v} /></div>
              <div className="p-3 flex items-center justify-between">
                <div className="font-heading text-sm text-white">{v.critic_name || v.user_name}</div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber text-obsidian font-heading">Critic</span>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* YouTube-suggested reviews */}
      {suggested.length > 0 && (
        <div className="mt-2">
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-heading mb-3 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Suggested reviews on YouTube
          </div>
          {loadingSugg ? (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-amber" /></div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {suggested.map((s) => (
                <div key={s.video_id} className="border border-white/10 rounded-md overflow-hidden bg-obsidian-surface/60" data-testid={`yt-suggested-${s.video_id}`}>
                  <div className="relative aspect-video bg-black">
                    <iframe
                      src={s.embed_url}
                      className="w-full h-full"
                      allowFullScreen
                      referrerPolicy="strict-origin-when-cross-origin"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      title={s.title}
                    />
                  </div>
                  <div className="p-3">
                    <div className="font-heading text-sm text-white line-clamp-2">{s.title}</div>
                    <div className="text-xs text-zinc-500 mt-1">{s.channel}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!loading && videos.length === 0 && suggested.length === 0 && (
        <p className="text-zinc-500 font-body italic text-sm">No reviews yet — be the first to share one.</p>
      )}
    </section>
  );
}
